import React, { forwardRef } from "react";
import DocumentDetailHeader from "./common/DocumentDetailHeader";
import DocumentDetail from "./common/DocumentDetail";
import {
  DocumentAttachmentDetailDto,
  DocumentDetailResponse,
  TagDto,
} from "apis/data-contracts";
import { SignInfo } from "./common/DocumentDetailSignSection";
import { Document, Page, View } from "@react-pdf/renderer";

interface DocumentViewerProps {
  formComponent: React.ReactNode;
  documentClassName: string;
  documentApproveStatus: DocumentDetailResponse["approveStatus"];
  documentFileUrl?: string;
  companyName: string;
  tags: TagDto[];
  createdUserInfo: SignInfo;
  approvedUserInfo?: SignInfo;
  showApproverSign?: boolean;
  attachmentList?: DocumentAttachmentDetailDto[];
  approveComment?: string;
  compactHeader?: boolean;
  flowFooter?: boolean;
}

const DocumentViewer = forwardRef<HTMLDivElement, DocumentViewerProps>(
  ({
    formComponent,
    documentClassName,
    documentApproveStatus,
    companyName,
    tags,
    createdUserInfo,
    approvedUserInfo,
    showApproverSign,
    attachmentList,
    approveComment,
    compactHeader,
    flowFooter,
  }) => {
    const hasAttachmentOrComment =
      (attachmentList?.length ?? 0) > 0 || !!approveComment?.trim();

    // 태그 + 작성/승인 서명은 한 덩어리로 묶어 페이지 중간에서 쪼개지지 않게 한다.
    const signatureBlock = (
      <View
        wrap={false}
        style={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          gap: "8mm",
          borderTop: "0.5mm solid #4E2D36",
          paddingTop: "4mm",
        }}
      >
        <DocumentDetail.Tags tags={tags} compact={flowFooter} />

        <DocumentDetail.SignSection
          showApproverSign={showApproverSign}
          documentApproveStatus={documentApproveStatus}
          createdUserInfo={createdUserInfo}
          approvedUserInfo={approvedUserInfo}
          compact={flowFooter}
        />
      </View>
    );

    // 첨부파일/코멘트는 서명과 분리해 필요 시 다음 페이지로 흘러가도록 둔다.
    // wrap={false}: 한 페이지에 다 못 담으면 일부만 남기지 않고 통째로 다음 페이지로 넘긴다.
    const attachmentCommentBlock = hasAttachmentOrComment ? (
      <View
        wrap={false}
        style={{
          display: "flex",
          flexDirection: "row",
          gap: "10mm",
        }}
      >
        <View style={{ flex: 1 }}>
          <DocumentDetail.ViewerAttachmentList
            attachmentList={attachmentList ?? []}
          />
        </View>
        <View
          style={{
            flex: 1,
          }}
        >
          <DocumentDetail.Comment comment={approveComment ?? ""} />
        </View>
      </View>
    ) : null;

    return (
      <Document>
        <Page
          size="A4"
          style={{
            display: "flex",
            flexDirection: "column",
            padding: "12mm 10mm",
            gap: "8mm",
            transform: "scale(1)",
            transformOrigin: "top left",
          }}
        >
          <View
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "11mm",
            }}
          >
            <DocumentDetailHeader
              documentClassName={documentClassName}
              companyName={companyName}
              compact={compactHeader}
            />

            <View>{formComponent}</View>
            {flowFooter ? (
              // 연속 미리보기: 서명은 한 덩어리로 유지하되, 첨부파일은
              // 자리가 부족하면 다음 페이지로 넘어가도록 wrap 가능한 컬럼으로 감싼다.
              <View
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "8mm",
                }}
              >
                {signatureBlock}
                {attachmentCommentBlock}
              </View>
            ) : null}
          </View>

          {!flowFooter && (
            <View
              wrap={false}
              style={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
              }}
            >
              <View style={{ flexGrow: 1 }} />
              <View
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "8mm",
                }}
              >
                {signatureBlock}
                {attachmentCommentBlock}
              </View>
            </View>
          )}
        </Page>
      </Document>
    );
  },
);
DocumentViewer.displayName = "DocumentViewer";

export default DocumentViewer;
